import { Component, Input, OnInit } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { TranslocoService } from '@ngneat/transloco';
import { Notification } from 'src/API';
import { PatientService } from 'src/app/core/api/patient.service';
import { QuestionnaireRequestService } from 'src/app/core/api/questionnaire-request.service';
import { StaffService } from 'src/app/core/api/staff.service';
import { ReferralService } from 'src/app/referral/referral.service';
import { QuestionnaireResponseModalComponent } from 'src/app/shared/consult-forms/questionnaire-response-modal/questionnaire-response-modal.component';
import { InboxModalComponent } from '../inbox-modal/inbox-modal.component';
import { NotificationFormat } from './../inbox.model';

@Component({
  selector: 'csi-notification-content-display',
  templateUrl: './notification-content-display.component.html',
  styleUrls: ['./notification-content-display.component.css']
})
export class NotificationContentDisplayComponent implements OnInit {
  private _notification: Notification;
  public notificationContent = null;
  public NotificationFormat = NotificationFormat;
  public customScoreIndicator: boolean;
  public customFlaggingText =
    '<span style="color: rgb(238, 99, 49);font-weight: bold;"> Based on the custom clinic questionnaire score, this patient is at risk.</span>';
  public safeTextBodyContent: SafeHtml | null = null;
  patientIdExists = false;
  constructor(
    public router: Router,
    private questionnaireRequestService: QuestionnaireRequestService,
    private referralService: ReferralService,
    public inboxDialogRef: MatDialogRef<InboxModalComponent>,
    public dialog: MatDialog,
    private sanitizer: DomSanitizer,
    private translocoService: TranslocoService,
    public patientService: PatientService,
    public staffService: StaffService
  ) {}
  @Input()
  set notification(value: Notification) {
    this._notification = value;
    if (this._notification && this._notification.content) {
      this.notificationContent = JSON.parse(this._notification.content);
      this.patientIdExists = !!(this.notificationContent && this.notificationContent.patientId);
      this.setCustomScoreIndicator();

      let textBody = (this.notificationContent && this.notificationContent.textBody) || '';
      let subject = (this.notificationContent && this.notificationContent.subject) || '';

      if (this.notification.type === 'questionnaire') {
        textBody = this.parseQuestionnaireText(textBody);
        subject = this.translocoService.translate('patientFilledQuestionnaire');
      } else if (this.notification.type === 'intake') {
        textBody = this.parseIntakeText(textBody);
        subject = this.parseIntakeSubject(subject);
      }

      this.safeTextBodyContent = this.sanitizer.bypassSecurityTrustHtml(
        this.customScoreIndicator ? textBody + this.customFlaggingText : textBody
      );

      this.notificationContent.subject = subject;
    }
  }
  ngOnInit() {}

  get notification(): Notification {
    return this._notification;
  }

  setCustomScoreIndicator() {
    this.customScoreIndicator = this.questionnaireRequestService.customSpeedThreshold(
      JSON.parse(this.notificationContent.questionnaireScores || '{}').SPEED
    );
  }

  parseQuestionnaireText(text: string): string {
    if (!text.startsWith('Patient ') || !text.includes(' just completed a dry eye questionnaire')) {
      return text;
    }

    const nameEndIndex = text.indexOf(' just completed');
    const patientName = text.substring(8, nameEndIndex);

    const hasResults = text.includes(' with the following results:');

    const translatedPrefix = this.translocoService.translate('Patient');
    const translatedAction = hasResults
      ? this.translocoService.translate('completedDryEyeQuestionnaireWithResults')
      : this.translocoService.translate('completedDryEyeQuestionnaire');

    const resultsText = hasResults
      ? text.substring(text.indexOf(' with the following results:') + 29)
      : '';

    return `${translatedPrefix} ${patientName} ${translatedAction}${resultsText}`;
  }

  parseIntakeText(text: string): string {
    if (!text.endsWith(' has submitted their patient intake form.')) {
      return text;
    }

    const patientName = text.replace(' has submitted their patient intake form.', '');
    return this.translocoService.translate('intakeFormSubmittedBody', { patientName });
  }

  parseIntakeSubject(subject: string): string {
    if (!subject.startsWith('New Patient Intake Form submitted by ')) {
      return subject;
    }

    const patientName = subject.replace('New Patient Intake Form submitted by ', '');
    return this.translocoService.translate('intakeFormSubmittedBy', { patientName });
  }

  viewQuestionnaireResponse() {
    this.questionnaireRequestService
      .getQuestionnaireRequest(this.notificationContent.id)
      .subscribe(questionnaire => {
        QuestionnaireResponseModalComponent.open(this.dialog, questionnaire);
      });
  }

  goToReferral() {
    this.referralService.currentReferral = null;
    const url = `/refer/${this.notificationContent.id}`;
    this.router.navigateByUrl(url);
    this.inboxDialogRef.close();
  }

  goToIntakeForm() {
    const url = `/intake-forms/${this.notificationContent.id}`;
    this.router.navigateByUrl(url);
    this.inboxDialogRef.close();
  }

  goToPatient() {
    const patientId = this.notificationContent.patientId;

    if (patientId) {
      this.router.navigateByUrl(`/patients/${patientId}`);
      this.inboxDialogRef.close();
    } else {
      console.warn('no patientId found in notification content');
    }
  }
}
